import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {Button} from '@pb/kit-component-button';
import {Calendar} from '@pb/kit-component-calendar';
import {Group} from '@pb/kit-component-group';
import {Input} from '@pb/kit-component-input-v2';
import {Loading} from '@pb/kit-component-loading';
import {PaginationChangeHandler, PaginationStripe} from '@pb/kit-component-pagination';
import {Scroll} from '@pb/kit-component-scroll';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import {H2, H3} from '@pb/kit-component-typography';

import {Holiday} from '/src/openapi/ccdb-bff/models/Holiday';

import {DEFAULT_PAGE_SIZE, REGION_OPTIONS, useHolidaySearch} from './useHolidaySearch';

import {
    actionLink,
    actionLinkDestructive,
    actionLinkDisabled,
    confirmActions,
    confirmDialog,
    confirmOverlay,
    datePickerCell,
    emptyState,
    errorMessage,
    holidayContainer,
    resultsSection,
    searchBar,
    searchInput,
    successMessage,
} from './holiday.styles.module.styl';

// ──────── Types ────────

type EditingRow = {
    id: number;
    holidayDate: Date | null;
    holidayName: string;
    region: string;
};

type NewRow = {
    holidayDate: Date | null;
    holidayName: string;
    region: string;
};

// ──────── Helpers ────────

/** Parse an ISO / UTC date string into a local Date, or return null. */
const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    try {
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? null : d;
    } catch {
        return null;
    }
};

/** Format a Date for display as MM/DD/YYYY. */
const formatDateForDisplay = (dateStr: string): string => {
    if (!dateStr) return '';
    const d = parseDate(dateStr);
    if (!d) return dateStr;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
};

/** Convert a Date object to an ISO string for the API. */
const formatDateForApi = (date: Date | null): string => {
    if (!date) return '';
    return date.toISOString();
};

// ──────── Shared inline styles (matches Company Search) ────────

const selectStyle = {
    height: 36,
    padding: '0 12px',
    borderRadius: 4,
    border: '1px solid #ccc',
    fontSize: 14,
    minWidth: 180,
};

const inlineInputStyle = {
    width: '100%',
    padding: '2px 6px',
    border: '1px solid #ccc',
    borderRadius: 4,
    fontSize: 14,
    boxSizing: 'border-box' as const,
};

const labelStyle = {display: 'block' as const, marginBottom: 4, fontSize: 12, fontWeight: 600};

// ──────── Component ────────

const HolidaySearchView: React.FC = () => {
    const {
        searchResults,
        loading,
        saving,
        deleting,
        error,
        successMessage: storeSuccessMessage,
        doSearch,
        doAdd,
        doUpdate,
        doDelete,
    } = useHolidaySearch();

    // Search filter state — both optional
    const [selectedRegion, setSelectedRegion] = useState('');
    const [searchYear, setSearchYear] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    // Inline editing state
    const [editingRow, setEditingRow] = useState<EditingRow | null>(null);

    // New holiday row state
    const [newRow, setNewRow] = useState<NewRow>({holidayDate: null, holidayName: '', region: 'UK'});

    // Delete confirmation state
    const [deleteTarget, setDeleteTarget] = useState<Holiday | null>(null);

    // Success message (local, dismissed on next action)
    const [localSuccess, setLocalSuccess] = useState<string | null>(null);

    // Sync store success message to local state
    useEffect(() => {
        if (storeSuccessMessage) {
            setLocalSuccess(storeSuccessMessage);
        }
    }, [storeSuccessMessage]);

    // Clear success after 4 s
    useEffect(() => {
        if (localSuccess) {
            const timer = setTimeout(() => setLocalSuccess(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [localSuccess]);

    // ── Derive a parsed year (undefined when blank / invalid) ──
    const parsedYear = useMemo(() => {
        const n = parseInt(searchYear, 10);
        return !isNaN(n) && n >= 1900 && n <= 2100 ? n : undefined;
    }, [searchYear]);

    // ── Load first page on mount ──
    useEffect(() => {
        doSearch({region: '', year: undefined, page: 0, size: pageSize});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Re-search helper (shared by save / add / delete callbacks) ──
    const reSearch = useCallback(() => {
        doSearch({region: selectedRegion, year: parsedYear, page: currentPage, size: pageSize});
    }, [doSearch, selectedRegion, parsedYear, currentPage, pageSize]);

    // ------- Search Handler -------
    const handleSearch = useCallback(() => {
        setLocalSuccess(null);
        setEditingRow(null);
        setCurrentPage(0);
        doSearch({region: selectedRegion, year: parsedYear, page: 0, size: pageSize});
    }, [selectedRegion, parsedYear, pageSize, doSearch]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    }, [handleSearch]);

    const handleReset = useCallback(() => {
        setSelectedRegion('');
        setSearchYear('');
        setCurrentPage(0);
        setPageSize(DEFAULT_PAGE_SIZE);
        setEditingRow(null);
        setLocalSuccess(null);
    }, []);

    // ------- Pagination Handler -------
    const onPaginationChange: PaginationChangeHandler = useCallback(({currentPage: page, pageSize: size}) => {
        setCurrentPage(page);
        setPageSize(size);
        doSearch({region: selectedRegion, year: parsedYear, page, size});
    }, [selectedRegion, parsedYear, doSearch]);

    // ------- Inline Edit Handlers -------
    const startEdit = useCallback((holiday: Holiday) => {
        setEditingRow({
            id: holiday.id,
            holidayDate: parseDate(holiday.holidayDate),
            holidayName: holiday.holidayName,
            region: holiday.region,
        });
        setLocalSuccess(null);
    }, []);

    const cancelEdit = useCallback(() => {
        setEditingRow(null);
    }, []);

    const saveEdit = useCallback(async () => {
        if (!editingRow) return;
        const success = await doUpdate({
            id: editingRow.id,
            holidayDate: formatDateForApi(editingRow.holidayDate),
            holidayName: editingRow.holidayName,
            region: editingRow.region,
        });
        if (success) {
            setEditingRow(null);
            reSearch();
        }
    }, [editingRow, doUpdate, reSearch]);

    // ------- Add Handler -------
    const handleAdd = useCallback(async () => {
        if (!newRow.holidayDate || !newRow.holidayName.trim()) return;
        setLocalSuccess(null);
        const success = await doAdd({
            holidayDate: formatDateForApi(newRow.holidayDate),
            holidayName: newRow.holidayName,
            region: newRow.region,
        });
        if (success) {
            setNewRow({holidayDate: null, holidayName: '', region: selectedRegion || 'UK'});
            reSearch();
        }
    }, [newRow, selectedRegion, doAdd, reSearch]);

    // ------- Delete Handlers -------
    const confirmDeleteHoliday = useCallback((holiday: Holiday) => {
        setDeleteTarget(holiday);
        setLocalSuccess(null);
    }, []);

    const cancelDelete = useCallback(() => {
        setDeleteTarget(null);
    }, []);

    const executeDelete = useCallback(async () => {
        if (!deleteTarget) return;
        const success = await doDelete({id: deleteTarget.id});
        if (success) {
            setDeleteTarget(null);
            reSearch();
        }
    }, [deleteTarget, doDelete, reSearch]);

    // Keep new-row region in sync with search filter (when non-empty)
    useEffect(() => {
        if (selectedRegion) {
            setNewRow(prev => ({...prev, region: selectedRegion}));
        }
    }, [selectedRegion]);

    // Build a human-readable search-description for empty-state
    const searchDescription = useMemo(() => {
        const parts: string[] = [];
        if (selectedRegion) parts.push(selectedRegion);
        if (parsedYear) parts.push(String(parsedYear));
        return parts.length ? parts.join(' / ') : 'the current filters';
    }, [selectedRegion, parsedYear]);

    return (
        <div className={holidayContainer}>
            <H2>Holiday Management</H2>

            {/* ======== Search Bar (matches Company Search layout) ======== */}
            <Group direction="h" className={searchBar}>
                <div>
                    <label htmlFor="region-select" style={labelStyle}>
                        Region
                    </label>
                    <select
                        id="region-select"
                        value={selectedRegion}
                        onChange={e => setSelectedRegion(e.target.value)}
                        style={selectStyle}>
                        <option value="">All Regions</option>
                        {REGION_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <div className={searchInput}>
                    <label htmlFor="year-input" style={labelStyle}>
                        Year
                    </label>
                    <Input
                        id="year-input"
                        value={searchYear}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchYear(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="YYYY (optional)"
                    />
                </div>
                <Button onClick={handleSearch} disabled={loading}>
                    Search
                </Button>
                <Button onClick={handleReset} disabled={loading}>
                    Reset
                </Button>
            </Group>

            {/* ======== Feedback Messages ======== */}
            {error ? (
                <div className={errorMessage}>{error}</div>
            ) : null}

            {localSuccess ? (
                <div className={successMessage}>{localSuccess}</div>
            ) : null}

            {/* ======== Loading ======== */}
            {loading ? (
                <Group style={{justifyContent: 'center', padding: 40}}>
                    <Loading />
                </Group>
            ) : null}

            {/* ======== Results Table ======== */}
            {!loading && searchResults ? (
                <div className={resultsSection}>
                    <H3>Results: {searchResults.totalElements} holidays found</H3>

                    {searchResults.holidays.length === 0 ? (
                        <div className={emptyState}>
                            <div>📅</div>
                            <p>No holidays found for {searchDescription}.</p>
                        </div>
                    ) : (
                        <>
                        <Scroll>
                            <Table>
                                <Head>
                                    <Cell>Holiday Date</Cell>
                                    <Cell>Holiday Name</Cell>
                                    <Cell>Region</Cell>
                                    <Cell>Options</Cell>
                                </Head>
                                <Body>
                                    {searchResults.holidays.map(holiday => {
                                        const isEditing = editingRow?.id === holiday.id;

                                        return (
                                            <Row key={holiday.id}>
                                                {/* ── Date Cell ── */}
                                                <Cell>
                                                    {isEditing ? (
                                                        <div className={datePickerCell}>
                                                            <Calendar
                                                                selected={editingRow.holidayDate ?? undefined}
                                                                onChange={(date?: Date) => setEditingRow({...editingRow, holidayDate: date ?? null})}
                                                                dateFormat="MM/dd/yyyy"
                                                                placeholderText="MM/DD/YYYY"
                                                            />
                                                        </div>
                                                    ) : (
                                                        formatDateForDisplay(holiday.holidayDate)
                                                    )}
                                                </Cell>
                                                {/* ── Name Cell ── */}
                                                <Cell>
                                                    {isEditing ? (
                                                        <input
                                                            type="text"
                                                            value={editingRow.holidayName}
                                                            onChange={e => setEditingRow({...editingRow, holidayName: e.target.value})}
                                                            style={inlineInputStyle}
                                                        />
                                                    ) : (
                                                        holiday.holidayName
                                                    )}
                                                </Cell>
                                                {/* ── Region Cell ── */}
                                                <Cell>
                                                    {isEditing ? (
                                                        <select
                                                            value={editingRow.region}
                                                            onChange={e => setEditingRow({...editingRow, region: e.target.value})}
                                                            style={selectStyle}>
                                                            {REGION_OPTIONS.map(opt => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        holiday.region
                                                    )}
                                                </Cell>
                                                {/* ── Actions Cell ── */}
                                                <Cell>
                                                    {isEditing ? (
                                                        <>
                                                            <span
                                                                className={saving ? actionLinkDisabled : actionLink}
                                                                onClick={saveEdit}
                                                                role="button"
                                                                tabIndex={0}
                                                                onKeyDown={e => e.key === 'Enter' && saveEdit()}>
                                                                Save
                                                            </span>
                                                            {' '}
                                                            <span
                                                                className={actionLink}
                                                                onClick={cancelEdit}
                                                                role="button"
                                                                tabIndex={0}
                                                                onKeyDown={e => e.key === 'Enter' && cancelEdit()}>
                                                                Cancel
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span
                                                                className={actionLink}
                                                                onClick={() => startEdit(holiday)}
                                                                role="button"
                                                                tabIndex={0}
                                                                aria-label={`Edit ${holiday.holidayName}`}
                                                                onKeyDown={e => e.key === 'Enter' && startEdit(holiday)}>
                                                                Edit
                                                            </span>
                                                            {' '}
                                                            <span
                                                                className={actionLinkDestructive}
                                                                onClick={() => confirmDeleteHoliday(holiday)}
                                                                role="button"
                                                                tabIndex={0}
                                                                aria-label={`Delete ${holiday.holidayName}`}
                                                                onKeyDown={e => e.key === 'Enter' && confirmDeleteHoliday(holiday)}>
                                                                Delete
                                                            </span>
                                                        </>
                                                    )}
                                                </Cell>
                                            </Row>
                                        );
                                    })}

                                    {/* ======== Add New Holiday Row ======== */}
                                    <Row>
                                        <Cell>
                                            <div className={datePickerCell}>
                                                <Calendar
                                                    selected={newRow.holidayDate ?? undefined}
                                                    onChange={(date?: Date) => setNewRow({...newRow, holidayDate: date ?? null})}
                                                    dateFormat="MM/dd/yyyy"
                                                    placeholderText="MM/DD/YYYY"
                                                />
                                            </div>
                                        </Cell>
                                        <Cell>
                                            <input
                                                type="text"
                                                value={newRow.holidayName}
                                                onChange={e => setNewRow({...newRow, holidayName: e.target.value})}
                                                placeholder="Holiday name"
                                                aria-label="New holiday name"
                                                style={inlineInputStyle}
                                            />
                                        </Cell>
                                        <Cell>
                                            <select
                                                value={newRow.region}
                                                onChange={e => setNewRow({...newRow, region: e.target.value})}
                                                style={selectStyle}>
                                                {REGION_OPTIONS.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </Cell>
                                        <Cell>
                                            <span
                                                className={saving || !newRow.holidayDate || !newRow.holidayName.trim() ? actionLinkDisabled : actionLink}
                                                onClick={handleAdd}
                                                role="button"
                                                tabIndex={0}
                                                aria-label="Add new holiday"
                                                onKeyDown={e => e.key === 'Enter' && handleAdd()}>
                                                Add
                                            </span>
                                        </Cell>
                                    </Row>
                                </Body>
                            </Table>
                        </Scroll>

                        <PaginationStripe
                            onPaginationChange={onPaginationChange}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            sizeSwitcherPosition="LEFT"
                            sizes={[20, 50, 100]}
                            totalCount={searchResults.totalElements}
                        />
                        </>
                    )}
                </div>
            ) : null}

            {/* ======== Initial State ======== */}
            {!loading && !searchResults && !error && (
                <div className={emptyState}>
                    <div>📅</div>
                    <p>Select a region and/or year, then click Search to view holidays.</p>
                </div>
            )}

            {/* ======== Delete Confirmation Dialog ======== */}
            {deleteTarget ? (
                <div className={confirmOverlay} onClick={cancelDelete} role="presentation">
                    <div
                        className={confirmDialog}
                        onClick={e => e.stopPropagation()}
                        role="alertdialog"
                        aria-modal="true"
                        aria-labelledby="delete-dialog-title"
                        aria-describedby="delete-dialog-desc"
                        onKeyDown={e => e.key === 'Escape' && cancelDelete()}
                        tabIndex={-1}
                    >
                        <H3 id="delete-dialog-title">Confirm Delete</H3>
                        <p id="delete-dialog-desc" style={{margin: '0 0 20px', fontSize: 14, color: '#444'}}>
                            Are you sure you want to delete <strong>{deleteTarget.holidayName}</strong> ({formatDateForDisplay(deleteTarget.holidayDate)})?
                        </p>
                        <div className={confirmActions}>
                            <Button onClick={cancelDelete} disabled={deleting}>
                                Cancel
                            </Button>
                            <Button onClick={executeDelete} disabled={deleting}>
                                {deleting ? 'Deleting…' : 'Delete'}
                            </Button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default HolidaySearchView;
